import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { GrIcon } from "../../icons";
import { palette } from "../../../theme/palette";
import { typography } from "../../../theme/typography";
import { Collapse } from "@mui/material";
import classNames from "classnames";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
const MemoedTreeNodeList = memo(GrTreeNodeList);
const MemoedTreeNodeContainer = memo(GrTreeNodeContainer);
const MemoedTreeNode = memo(GrTreeNode);
export function GrTree(props) {
    return (_jsx(GrTreeNodeList, { data: props.data, nodeDef: props.nodeDef, depth: 0, blur: props.blur, onChildClick: () => {
            var _a;
            (_a = props.onFoucs) === null || _a === void 0 ? void 0 : _a.call(props);
        } }));
}
function GrTreeNodeList(props) {
    var _a, _b;
    const idFieldName = (_a = props.nodeDef.idFieldName) !== null && _a !== void 0 ? _a : "id";
    const [selectedRowIdx, setSelectedRowIdx] = useState(undefined);
    const onClickIndexed = useCallback((index) => {
        setSelectedRowIdx(index);
        if (props.onChildClick) {
            props.onChildClick(index);
        }
    }, [props.onChildClick]);
    useEffect(() => {
        if (!props.focus) {
            setSelectedRowIdx(undefined);
        }
    }, [props.focus]);
    return (_jsx(_Fragment, { children: (_b = props.data) === null || _b === void 0 ? void 0 : _b.map((node, index) => (_jsx(MemoedTreeNodeContainer, { node: node, nodeDef: props.nodeDef, depth: props.depth, focus: !props.blur && (props.focus || props.depth === 0) && selectedRowIdx === index, onClickIndexed: onClickIndexed, index: index }, node[idFieldName]))) }));
}
function GrTreeNodeContainer(props) {
    const onClick = useCallback(() => {
        if (props.onClickIndexed) {
            props.onClickIndexed(props.index);
        }
    }, [props.onClickIndexed, props.index]);
    return _jsx(MemoedTreeNode, { data: props.node, nodeDef: props.nodeDef, depth: props.depth, focus: props.focus, onClick: onClick });
}
function GrTreeNode(props) {
    var _a, _b, _c, _d;
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [blur, setBlur] = useState(false);
    const idFieldName = (_a = props.nodeDef.idFieldName) !== null && _a !== void 0 ? _a : "id";
    const childrenFieldName = (_b = props.nodeDef.childrenFieldName) !== null && _b !== void 0 ? _b : "children";
    const labelFieldName = (_c = props.nodeDef.labelFieldName) !== null && _c !== void 0 ? _c : "label";
    const id = props.data[idFieldName];
    const label = props.data[labelFieldName];
    const childrenDef = props.nodeDef.childNodeDef;
    const children = props.data[childrenFieldName];
    const nodeEntity = { data: props.data, label, id, children };
    const labelForRender = props.nodeDef.labelGetter ? props.nodeDef.labelGetter(nodeEntity) : String(label);
    if (id === undefined || id === null || label === undefined || label === null) {
        throw new Error(`id or label is not defined. id: ${id}, label: ${label}`);
    }
    if (typeof id !== "string" || typeof label !== "string") {
        throw new Error(`id or label is not string. id: ${id}, label: ${label}`);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClick(e) {
        var _a, _b;
        setBlur(true);
        if (props.nodeDef.childNodeDef) {
            e.preventDefault();
        }
        if (props.onClick) {
            props.onClick();
        }
        if (props.nodeDef.onClick) {
            (_b = (_a = props.nodeDef).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e, nodeEntity);
        }
    }
    const isFocused = props.nodeDef.isFocused ? props.nodeDef.isFocused(nodeEntity) : props.focus;
    // TODO: 일정 상 디자인만 구현, 전체적인 구조를 다시 만들 때 같이 수정해주세요.
    const rowStyle = useMemo(() => ({
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        padding: "4px 4px 4px 20px",
        marginBottom: "6px",
        cursor: "pointer",
        backgroundColor: isFocused ? palette.blue015 : hovered ? palette.bg020 : palette.white,
        color: isFocused ? palette.blue060 : palette.grey090,
        borderRadius: 4,
    }), [hovered, isFocused]);
    const iconStyle = useMemo(() => {
        return {
            width: "24px",
            height: "24px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            marginRight: "4px",
            flexShrink: 0,
        };
    }, []);
    const blockStyle = useMemo(() => {
        var _a;
        return {
            width: `${((_a = props.depth) !== null && _a !== void 0 ? _a : 0) * 16}px`,
            height: "24px",
            flexShrink: 0,
        };
    }, []);
    const textStyle = useMemo(() => ({
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        ...typography[isFocused ? "Content.Subtitle04" : "Content.Body04"],
    }), [isFocused]);
    const onChildClick = useCallback(() => {
        setBlur(false);
        if (props.onClick) {
            props.onClick();
        }
    }, [props.onClick]);
    return (_jsxs("div", { className: classNames("GrTree", props.className), children: [_jsx("div", { onClick: handleClick, children: _jsxs("div", { style: rowStyle, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("div", { style: blockStyle }), _jsx("div", { style: iconStyle, onClick: e => {
                                setOpen(p => !p);
                                e.stopPropagation();
                            }, children: childrenDef ? (_jsx(GrIcon, { type: open ? "caret-down-filled" : "caret-right-filled", size: 14 })) : (_jsx(GrIcon, { type: "point-filled", size: 10 })) }), _jsxs("div", { style: { ...textStyle, ...props.nodeDef.style }, children: [props.nodeDef.renderFunc && props.nodeDef.renderFunc(nodeEntity), !props.nodeDef.renderFunc && _jsx(_Fragment, { children: labelForRender })] })] }) }), childrenDef && (_jsx(Collapse, { in: open, children: open && (_jsx(MemoedTreeNodeList, { data: children, nodeDef: childrenDef, depth: ((_d = props.depth) !== null && _d !== void 0 ? _d : 0) + 1, blur: blur, focus: isFocused, onChildClick: onChildClick })) }))] }));
}
//# sourceMappingURL=GrTree.js.map